import React, { useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Box, Flex } from 'rebass/styled-components';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useConsumerSignupV2 } from 'src/state/actions/users';
import { Button } from 'shared/components';
import { DefaultInput, DateInput, PhoneNumberInput } from 'shared/components/text-input-with-label';
import {
  CheckoutBody,
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import useErnie from 'shared/hooks/use-ernie';
import { Divider } from 'src/components/divider';
import { GoogleLogin, AppleLogin } from 'src/components/social-login';
import { useTurnstile } from 'src/components/turnstile';
import { TURNSTILE_ACTIONS } from 'shared/constants/turnstile';
import { Terms } from './terms';

async function handleSignup(setLoading, Cart, setInputFieldError, showErnie, consumerSignup, UI, turnstileToken = '') {
  // TODO: move this to a validator
  setLoading(true);
  const canSubmit = Cart.validateAuth();

  if (!canSubmit.proceed) {
    const { errors } = canSubmit;
    setInputFieldError([...errors]);
    if (errors && errors.length > 1) {
      showErnie('Please fill out the missing information.', 'danger');
    } else if (errors && errors.length) {
      const errorMessage = errors[0].message;
      showErnie(errorMessage, 'danger');
    } else {
      showErnie('We are unable to create your account. Please contact support.', 'danger');
    }
    setLoading(false);
    return;
  }

  const { email, password } = Cart.order.inlineAuth;
  const birthday = Cart.order.inlineAuth.birthdate;
  const profile = {
    email,
    password,
    profile: {
      ..._.pick(Cart.order.inlineAuth, ['firstName', 'lastName', 'phone']),
      birthday,
      textNotifications: true,
      emailNotifications: true,
    },
  };
  const userCreationData = {
    dispensaryId: Cart.order?.dispensary?.id,
    embedded: UI.isVariant(['embedded', 'store-front']),
  };

  try {
    const signupResult = await consumerSignup({ profile, userCreationData, turnstileToken });
    setLoading(false);
    if (signupResult.success) {
      showErnie('Success! Your account has been created.', 'success');
    } else {
      console.error(signupResult);
      if (_.includes(signupResult.error.message, 'email already exists')) {
        showErnie('An account with that email already exists.', 'danger');
      } else {
        showErnie('We are unable to create your account. Please contact support.', 'danger');
      }
    }
  } catch (e) {
    console.error(e);
    showErnie('We are unable to create your account. Please contact support.', 'danger');
  }
}

function InlineSignup({ onShowLogin, inputFieldError, setInputFieldError, handleInputChange }) {
  const [loading, setLoading] = useState(false);
  const { Cart, UI } = useStores();
  const Checkout = useCheckout();
  const showErnie = useErnie();
  const consumerSignup = useConsumerSignupV2();
  const flags = useFlags();
  const isTurnstileEnabled = flags['growth.ecomm.signup-turnstile-enable.rollout'] ?? false;

  const { TurnstileComponent, executeAndWaitForToken } = useTurnstile({
    size: 'flexible',
    action: TURNSTILE_ACTIONS.SIGNUP,
  });

  function handleShowLogin() {
    const { inlineAuth } = Cart?.order;
    Checkout.removeError({ path: 'inlineAuth' });
    if (inlineAuth) {
      inlineAuth.firstName = '';
      inlineAuth.lastName = '';
      inlineAuth.phone = '';
      inlineAuth.birthdate = '';
      inlineAuth.password = '';
      inlineAuth.email = '';
    }
    onShowLogin();
  }

  const inlineAuthError = Checkout.getError({ card: 'inline-auth' }).error;
  const removeInlineAuthError = () => Checkout.removeError({ card: 'inline-auth' });
  const getInputFieldError = (field) => inlineAuthError || _.find(inputFieldError, ['field', field]);

  const handleOnSubmit = async () => {
    if (isTurnstileEnabled) {
      try {
        const token = await executeAndWaitForToken();
        await handleSignup(setLoading, Cart, setInputFieldError, showErnie, consumerSignup, UI, token);
      } catch (error) {
        console.error(error);
      }
    } else {
      await handleSignup(setLoading, Cart, setInputFieldError, showErnie, consumerSignup, UI);
    }
  };

  return (
    <CheckoutLabelGrouping id='inlineAuth'>
      <CheckoutLabel card>
        <CheckoutSectionHeading pt='14px'>Sign Up</CheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        <Box pt='16px' pb='20px'>
          <InstructionText>
            To continue, create an account below. Already have one?{' '}
            <LoginButton tabIndex={0} onClick={handleShowLogin}>
              Login
            </LoginButton>
          </InstructionText>
        </Box>
        <Container>
          <SocialLogins>
            <GoogleLogin redirectUrl={window.location.href} />
            <AppleLogin redirectUrl={window.location.href} />
          </SocialLogins>

          <Divider text='or enter your information' />

          <NameRow>
            <DefaultInput
              onValueAfterError={removeInlineAuthError}
              error={getInputFieldError('firstName')}
              errorMessage={Checkout.getError({ path: 'firstName' }).message}
              name='firstName'
              label='First Name'
              onChange={handleInputChange}
              value={Cart.order?.inlineAuth?.firstName}
              mt={0}
              mr='15px'
              width='50%'
              autoComplete='given-name'
            />
            <DefaultInput
              onValueAfterError={removeInlineAuthError}
              error={getInputFieldError('lastName')}
              errorMessage={Checkout.getError({ path: 'lastName' }).message}
              name='lastName'
              label='Last Name'
              onChange={handleInputChange}
              value={Cart.order?.inlineAuth?.lastName}
              mt={0}
              width='50%'
              autoComplete='family-name'
            />
          </NameRow>
          <PhoneNumberInput
            onValueAfterError={removeInlineAuthError}
            error={getInputFieldError('phone')}
            errorMessage={Checkout.getError({ path: 'phone' }).message}
            name='phone'
            mb='19px'
            width='100%'
            label='Mobile Phone'
            value={Cart.order?.inlineAuth?.phone}
            onChange={handleInputChange}
            autoComplete='tel'
          />
          <DateInput
            onValueAfterError={removeInlineAuthError}
            error={getInputFieldError('birthdate')}
            errorMessage={Checkout.getError({ path: 'birthdate' }).message}
            name='birthdate'
            onChange={handleInputChange}
            width='100%'
            label='Birthdate'
            autoComplete='bday'
          />
          <DefaultInput
            onValueAfterError={removeInlineAuthError}
            error={getInputFieldError('email')}
            errorMessage={Checkout.getError({ path: 'email' }).message}
            name='email'
            label='Email'
            onChange={handleInputChange}
            value={Cart.order?.inlineAuth?.email}
            mb='19px'
            mt='20px'
            width='100%'
            autoComplete='email'
          />
          <DefaultInput
            onValueAfterError={removeInlineAuthError}
            error={getInputFieldError('password')}
            errorMessage={Checkout.getError({ path: 'password' }).message}
            name='password'
            type='password'
            label='Password'
            onChange={handleInputChange}
            value={Cart.order?.inlineAuth?.password}
            width='100%'
            mb='19px'
            autoComplete='new-password'
          />
          <Terms />
          {isTurnstileEnabled && (
            <Box mt='20px'>
              <TurnstileComponent />
            </Box>
          )}
          <Flex alignItems='center' justifyContent='space-between' mt='22px' mb='8px'>
            <Button onClick={handleOnSubmit} loading={loading}>
              Create Account
            </Button>
          </Flex>
        </Container>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

export default observer(InlineSignup);

const InstructionText = styled.div`
  color: #6b6f73;
  font-size: 13px;
  line-height: normal;

  a {
    color: ${({ theme }) => theme.customized.colors.buttonsLinks};
    cursor: pointer;
  }
`;

const Container = styled.div`
  max-width: 390px;
`;

const SocialLogins = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 19px;
`;

const LoginButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  cursor: pointer;
  padding-left: 0;
`;
